import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router, UrlMatcher } from '@angular/router';

export interface RouteAirport {
  city: string;
  iata: string;
  airport: string;
  country: string;
}

export interface RouteFaq {
  q: string;
  a: string;
}

/** One flight route landing page, e.g. /flights/nairobi-to-mombasa. */
export interface FlightRouteContent {
  slug: string;
  /** Hero photo, relative to the base href (assets/img/routes/*.webp); null uses the brand gradient. */
  image: string | null;
  from: RouteAirport;
  to: RouteAirport;
  title: string;
  description: string;
  h1: string;
  distanceKm: number;
  flightTime: string;
  airlines: string[];
  intro: string[];
  highlights: string[];
  tips: string[];
  faq: RouteFaq[];
  busUrl: string | null;
  related: string[];
}

/** Element id of the landing page's search widget section (the sidebar CTA scrolls to it). */
export const ROUTE_SEARCH_ID = 'route-search';

let routesPromise: Promise<readonly FlightRouteContent[]> | null = null;

/**
 * Landing page content, in priority order. Loaded as its own chunk so the copy
 * stays out of the initial bundle. The same JSON feeds scripts/seo-prerender.mjs,
 * which writes the static HTML and sitemap, so crawlers and the running app
 * always see the same copy.
 */
export function loadFlightRoutes(): Promise<readonly FlightRouteContent[]> {
  routesPromise ??= import('./flight-routes.json').then(module => (module.default as { routes: FlightRouteContent[] }).routes);
  return routesPromise;
}

export function isInternational(route: FlightRouteContent): boolean {
  return route.from.country !== route.to.country;
}

const ROUTE_SLUG = /^[a-z]+(?:-[a-z]+)*-to-[a-z]+(?:-[a-z]+)*$/;

/** Matches single-segment `<from>-to-<to>` URLs; the resolver decides whether the route exists. */
export const flightRouteMatcher: UrlMatcher = segments =>
  segments.length === 1 && ROUTE_SLUG.test(segments[0].path)
    ? { consumed: segments, posParams: { slug: segments[0] } }
    : null;

/** Resolves the landing page content, or sends unknown routes to the home page. */
export const flightRouteResolver: ResolveFn<FlightRouteContent | RedirectCommand> = async route => {
  const router = inject(Router);
  const slug = route.paramMap.get('slug') ?? '';
  const content = (await loadFlightRoutes()).find(item => item.slug === slug);
  return content ?? new RedirectCommand(router.parseUrl('/'));
};
